const personaFiles = import.meta.glob('/src/prompts/personas/*.md', {
    eager: true,
    import: 'default',
});

/**
 * P1.4: shared output contract (format, measurements, language, anti-slop,
 * structured appendix spec). Loaded separately from personas and appended to
 * the generation prompt so formatting rules are stated ONCE, not repeated in
 * every persona file. The glob above picks it up too, but PERSONA_FILE_MAP
 * does not map it, so it can never be loaded as a persona.
 */
const OUTPUT_CONTRACT_PATH = '/src/prompts/personas/_output-contract.md';

let outputContractCache: string | null = null;

export function loadOutputContract(): string {
    if (outputContractCache !== null) {
        return outputContractCache;
    }
    const content = personaFiles[OUTPUT_CONTRACT_PATH] as string | undefined;
    outputContractCache = content && content.trim() !== '' ? content.trim() : '';
    return outputContractCache;
}

const PERSONA_FILE_MAP: Record<string, string> = {
    'TestKitchenScientist': 'TestKitchenScientist.md',
    'TheCulinaryAlchemist': 'TheCulinaryAlchemist.md',
    'TheHeritageChef': 'TheHeritageChef.md',
    'TheEssentialistChef': 'TheEssentialistChef.md',
    'TheElementalChef': 'TheElementalChef.md',
    'TheVitalityChef': 'TheVitalityChef.md',
};

/**
 * Marker that separates a persona's chat profile (above) from the
 * recipe-generation prompt (below). The chat loader reads everything up to
 * this marker; the generation loader reads the whole file.
 *
 * Adding this marker to an existing persona markdown is a non-breaking change:
 * personas without the marker fall back to returning the full file (current
 * behavior). This means old markdowns keep working until they're updated.
 */
const CHAT_PROFILE_END_MARKER = '<!-- CHAT_PROFILE_END -->';

const personaCache: Map<string, string> = new Map();
const personaProfileCache: Map<string, string> = new Map();
const personaGenerationCache: Map<string, string> = new Map();

/**
 * Load the FULL persona markdown (used for recipe generation).
 * Includes the chat profile + the generation methodology + blueprint.
 */
export function loadPersonaMarkdown(personaId: string): string {
    if (personaCache.has(personaId)) {
        return personaCache.get(personaId)!;
    }

    const fileName = PERSONA_FILE_MAP[personaId];
    if (!fileName) {
        return getDefaultPersonaPrompt();
    }

    const filePath = `/src/prompts/personas/${fileName}`;
    const content = personaFiles[filePath] as string | undefined;

    if (!content) {
        return getDefaultPersonaPrompt();
    }

    personaCache.set(personaId, content);
    return content;
}

/**
 * Load the persona's CHAT profile only — voice, tone, philosophy, character sketch.
 * Excludes the recipe-generation methodology + blueprint (everything below the
 * CHAT_PROFILE_END marker).
 *
 * This is what should be injected into the chat system prompt so the AI stays
 * in character without being told how to write a recipe on every conversational
 * turn.
 *
 * Falls back to the full file (and a console warning) if the marker is missing,
 * so old markdowns without the marker keep working.
 */
export function loadPersonaChatProfile(personaId: string): string {
    if (personaProfileCache.has(personaId)) {
        return personaProfileCache.get(personaId)!;
    }

    const fullMarkdown = loadPersonaMarkdown(personaId);

    const markerIndex = fullMarkdown.indexOf(CHAT_PROFILE_END_MARKER);
    if (markerIndex === -1) {
        // Backwards compat: no marker means return the full file
        return fullMarkdown;
    }

    // Include everything up to (but not including) the marker, trimmed
    const profile = fullMarkdown.substring(0, markerIndex).trimEnd();
    personaProfileCache.set(personaId, profile);
    return profile;
}

/**
 * Load the persona's GENERATION prompt only — everything below the
 * CHAT_PROFILE_END marker (methodology + blueprint + voice). Excludes the
 * compact chat profile at the top of the file so chat-only instructions
 * never leak into recipe-generation prompts.
 *
 * Falls back to the full file if the marker is missing (backwards compat).
 */
export function loadPersonaGenerationPrompt(personaId: string): string {
    if (personaGenerationCache.has(personaId)) {
        return personaGenerationCache.get(personaId)!;
    }

    const fullMarkdown = loadPersonaMarkdown(personaId);
    const markerIndex = fullMarkdown.indexOf(CHAT_PROFILE_END_MARKER);
    if (markerIndex === -1) {
        // Backwards compat: no marker means the whole file is the generation prompt
        return fullMarkdown;
    }

    const generation = fullMarkdown.substring(markerIndex + CHAT_PROFILE_END_MARKER.length).trimStart();
    personaGenerationCache.set(personaId, generation);
    return generation;
}

function getDefaultPersonaPrompt(): string {
    return `# AI Chef Assistant

You are a helpful, knowledgeable cooking assistant. Your role is to:
- Answer questions about recipes, ingredients, and cooking techniques
- Provide substitution suggestions when asked
- Explain the 'why' behind cooking methods
- Be conversational, friendly, and educational

Always maintain a helpful, encouraging tone while being precise and accurate.`;
}

export function getAvailablePersonas(): string[] {
    return Object.keys(PERSONA_FILE_MAP);
}

export function isValidPersonaId(personaId: string): boolean {
    return personaId in PERSONA_FILE_MAP;
}

export function clearPersonaCache(): void {
    personaCache.clear();
    personaProfileCache.clear();
    personaGenerationCache.clear();
}
